import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { portalApiKeysApi, portalAnalyticsApi } from '../../services/api';
import { toast } from '../../stores';
import { usePortalStore } from '../../stores';
import { 
  NexusCard, NexusButton, NexusInput, NexusSelect,
  NexusModal, NexusModalFooter, NexusBadge, NexusTable 
} from '../../components/nexus-ui';
import { Plus, Key, Copy, Trash, Warning, Info, ChartBar } from '@phosphor-icons/react';

const PortalAPIKeys = () => {
  const queryClient = useQueryClient();
  const { user } = usePortalStore();
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    default_model: 'claude-opus-4-6',
    rate_limit_rpm: 60,
    rate_limit_tpd: 100000,
    expires_in_days: null,
  });
  
  const isAdmin = user?.role === 'admin';
  
  const { data: keys, isLoading } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => portalApiKeysApi.list().then(r => r.data),
  });

  // Per-key usage matrix — aggregates every /api/v1/* call that
  // carried an API-key auth header. Refreshes every 30s so operators
  // watching a live migration see numbers move without a manual
  // reload.
  const { data: usage } = useQuery({
    queryKey: ['apiKeysUsage'],
    queryFn: () => portalAnalyticsApi.getApiKeysUsage().then(r => r.data),
    refetchInterval: 30000,
  });
  
  const createMutation = useMutation({
    mutationFn: (data) => portalApiKeysApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['apiKeys']);
      setNewKey(response.data.api_key);
      setShowCreate(false);
      setFormData({ label: '', default_model: 'claude-opus-4-6', rate_limit_rpm: 60, rate_limit_tpd: 100000, expires_in_days: null });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create API key'),
  });
  
  const revokeMutation = useMutation({
    mutationFn: (keyId) => portalApiKeysApi.revoke(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries(['apiKeys']);
      toast.success('API key revoked');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to revoke'),
  });
  
  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard');
  };
  
  const columns = [
    { 
      key: 'label', 
      header: 'Label',
      render: (value, row) => (
        <div>
          <p className="font-medium text-nexus-text-primary">{value}</p>
          <code className="text-xs text-nexus-text-tertiary">{row.key_prefix}...</code>
          {row.default_model && (
            <p className="text-[10px] mt-0.5" style={{ color: '#F26B1F' }}>
              {row.default_model === 'claude-opus-4-8' ? 'Apex 4.8'
                : row.default_model === 'claude-opus-4-7' ? 'Ultra 4.7'
                : row.default_model === 'claude-opus-4-6' ? 'Pro 4.6'
                : row.default_model}
            </p>
          )}
        </div>
      )
    },
    { 
      key: 'rate_limit_rpm', 
      header: 'Rate Limits',
      render: (value, row) => (
        <div className="text-sm">
          <p className="text-nexus-text-primary">{value} req/min</p>
          <p className="text-nexus-text-tertiary">{row.rate_limit_tpd.toLocaleString()} tokens/day</p>
        </div>
      )
    },
    { 
      key: 'is_active', 
      header: 'Status',
      render: (value, row) => {
        if (!value) return <NexusBadge variant="error">Revoked</NexusBadge>;
        if (row.expires_at && new Date(row.expires_at) < new Date()) {
          return <NexusBadge variant="warning">Expired</NexusBadge>;
        }
        return <NexusBadge variant="success" pulse>Active</NexusBadge>;
      }
    },
    { 
      key: 'last_used_at', 
      header: 'Last Used',
      render: (value) => value 
        ? new Date(value).toLocaleDateString() 
        : <span className="text-nexus-text-tertiary">Never</span>
    },
    {
      key: 'id',
      header: '',
      sortable: false,
      render: (value, row) => isAdmin && row.is_active && (
        <button
          onClick={() => {
            if (window.confirm('Revoke this API key?')) {
              revokeMutation.mutate(value);
            }
          }}
          className="p-1 text-nexus-text-tertiary hover:text-nexus-error transition-colors"
          title="Revoke"
        >
          <Trash className="w-4 h-4" weight="duotone" />
        </button>
      )
    },
  ];
  
  return (
    <div className="space-y-6" data-testid="portal-api-keys">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-nexus-text-primary">
            API Keys
          </h2>
          <p className="text-nexus-text-secondary mt-1">
            Manage API keys for programmatic access
          </p>
        </div>
        
        {isAdmin && (
          <NexusButton onClick={() => setShowCreate(true)} data-testid="create-key-btn">
            <Plus className="w-4 h-4" weight="bold" />
            Create API Key
          </NexusButton>
        )}
      </div>
      
      {/* Info Card */}
      <NexusCard variant="glass" hover={false} className="p-4">
        <div className="flex items-start gap-3">
          <Key className="w-5 h-5 text-nexus-accent-mint flex-shrink-0 mt-0.5" weight="duotone" />
          <div>
            <p className="text-sm text-nexus-text-primary">
              Use API keys to integrate with your applications via REST API.
            </p>
            <p className="text-xs text-nexus-text-tertiary mt-1">
              Base URL: <code className="text-nexus-accent-mint">{process.env.REACT_APP_BACKEND_URL}/api/v1/chat</code>
            </p>
          </div>
        </div>
      </NexusCard>
      
      {/* Keys Table */}
      <NexusCard variant="glass" hover={false}>
        <div className="p-6">
          <NexusTable
            columns={columns}
            data={keys || []}
            loading={isLoading}
            emptyMessage="No API keys created yet"
          />
        </div>
      </NexusCard>
      
      {/* Create Modal */}
      <NexusModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create API Key"
        description="Generate a new API key for your application"
      >
        <div className="space-y-4">
          <NexusInput
            label="Label"
            value={formData.label}
            onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
            placeholder="e.g., Production App"
            data-testid="key-label-input"
          />
          <NexusSelect
            label="Default engine"
            options={[
              { value: 'claude-opus-4-6', label: 'Cognara Engine · Pro 4.6 (1× credits · balanced)' },
              { value: 'claude-opus-4-7', label: 'Cognara Engine · Ultra 4.7 (1.2× credits · premium)' },
              { value: 'claude-opus-4-8', label: 'Cognara Engine · Apex 4.8 (1.5× credits · flagship)' },
            ]}
            value={formData.default_model}
            onChange={(value) => setFormData(prev => ({ ...prev, default_model: value }))}
            data-testid="key-engine-select"
          />
          <p className="-mt-2 text-[11px] text-nexus-text-tertiary">
            Requests that omit the <code>model</code> body field will run on this engine. Callers can still override per request.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <NexusInput
              label="Rate Limit (req/min)"
              type="number"
              value={formData.rate_limit_rpm}
              onChange={(e) => setFormData(prev => ({ ...prev, rate_limit_rpm: parseInt(e.target.value) }))}
            />
            <NexusInput
              label="Token Limit (tokens/day)"
              type="number"
              value={formData.rate_limit_tpd}
              onChange={(e) => setFormData(prev => ({ ...prev, rate_limit_tpd: parseInt(e.target.value) }))}
            />
          </div>
          <NexusSelect
            label="Expiration"
            options={[
              { value: '', label: 'Never expires' },
              { value: '30', label: '30 days' },
              { value: '90', label: '90 days' },
              { value: '365', label: '1 year' },
            ]}
            value={formData.expires_in_days?.toString() || ''}
            onChange={(value) => setFormData(prev => ({ 
              ...prev, 
              expires_in_days: value ? parseInt(value) : null 
            }))}
          />
        </div>
        <NexusModalFooter>
          <NexusButton variant="ghost" onClick={() => setShowCreate(false)}>
            Cancel
          </NexusButton>
          <NexusButton 
            onClick={() => createMutation.mutate(formData)}
            isLoading={createMutation.isPending}
            data-testid="create-key-submit"
          >
            Create Key
          </NexusButton>
        </NexusModalFooter>
      </NexusModal>
      
      {/* New Key Modal */}
      <NexusModal
        isOpen={!!newKey}
        onClose={() => setNewKey(null)}
        title="API Key Created"
        closeOnBackdrop={false}
      >
        <div className="space-y-4">
          <div className="p-4 bg-nexus-accent-amber/10 border border-nexus-accent-amber/30 rounded-lg flex items-start gap-3">
            <Warning className="w-5 h-5 text-nexus-accent-amber flex-shrink-0" weight="fill" />
            <p className="text-sm text-nexus-text-primary">
              Copy this API key now. You won't be able to see it again!
            </p>
          </div>
          
          <div className="relative">
            <input
              type="text"
              value={newKey || ''}
              readOnly
              className="w-full bg-nexus-bg-primary border border-nexus-border-emphasized rounded-lg px-4 py-3 pr-12 text-nexus-text-primary font-mono text-sm"
            />
            <button
              onClick={() => copyKey(newKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-nexus-accent-mint hover:text-white transition-colors"
            >
              <Copy className="w-5 h-5" weight="duotone" />
            </button>
          </div>
        </div>
        <NexusModalFooter>
          <NexusButton onClick={() => setNewKey(null)}>
            Done
          </NexusButton>
        </NexusModalFooter>
      </NexusModal>
      
      {/* Rate-Limit Explainer — teaches operators what the numbers mean
          BEFORE they wire the key into a downstream product. Positioned
          right under the info card so it's the first thing seen on a
          fresh visit. */}
      <NexusCard variant="glass" hover={false} className="p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-nexus-accent-amber flex-shrink-0 mt-0.5" weight="duotone" />
          <div className="space-y-2">
            <p className="text-sm font-semibold text-nexus-text-primary">
              What do the rate limits mean?
            </p>
            <ul className="text-xs text-nexus-text-secondary space-y-1 list-disc pl-4">
              <li>
                <span className="text-nexus-text-primary font-medium">req/min</span> — the maximum number of API calls this key may make in any 60-second window. Extra calls receive a <code>429 Too Many Requests</code> until the window rolls forward.
              </li>
              <li>
                <span className="text-nexus-text-primary font-medium">tokens/day</span> — the ceiling on prompt+completion tokens per UTC day. Once hit, every subsequent call receives a <code>403</code> until midnight UTC.
              </li>
              <li>
                Each response you receive is still billed against your tenant's <span className="text-nexus-text-primary font-medium">credit wallet</span> using the standard 1 credit / 1,000-token base rate (multiplied by the engine tier). Rate limits are a <em>safety valve</em>, not a billing cap.
              </li>
            </ul>
          </div>
        </div>
      </NexusCard>

      {/* Usage Matrix — per-product spend tracker. Each row = one API key
          (i.e. one downstream product/integration). Refreshes every 30s.
          Empty until at least one /api/v1/* call has been recorded. */}
      <NexusCard variant="glass" hover={false}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ChartBar className="w-5 h-5 text-nexus-accent-mint" weight="duotone" />
              <h3 className="font-heading text-lg font-semibold text-nexus-text-primary">
                Usage matrix — by product
              </h3>
            </div>
            <span className="text-[11px] text-nexus-text-tertiary">Refreshes every 30s</span>
          </div>
          <p className="text-xs text-nexus-text-tertiary -mt-1">
            One row per API key. Credits shown at the 1&nbsp;credit&nbsp;/&nbsp;1,000-token base rate (before engine multiplier). Cost is the raw provider USD equivalent.
          </p>
          <NexusTable
            columns={[
              { key: 'label', header: 'Product / Label', render: (v, row) => (
                <div>
                  <p className="font-medium text-nexus-text-primary">{v}</p>
                  {row.key_prefix && <code className="text-xs text-nexus-text-tertiary">{row.key_prefix}...</code>}
                </div>
              ) },
              { key: 'calls', header: 'Calls', render: (v) => <span className="font-mono text-nexus-text-primary">{(v ?? 0).toLocaleString()}</span> },
              { key: 'input_tokens', header: 'Input tokens', render: (v) => <span className="font-mono text-nexus-text-secondary">{(v ?? 0).toLocaleString()}</span> },
              { key: 'output_tokens', header: 'Output tokens', render: (v) => <span className="font-mono text-nexus-text-secondary">{(v ?? 0).toLocaleString()}</span> },
              { key: 'tokens', header: 'Total tokens', render: (v) => <span className="font-mono text-nexus-text-primary">{(v ?? 0).toLocaleString()}</span> },
              { key: 'credits', header: 'Credits (base)', render: (v) => <span className="font-mono" style={{ color: '#F26B1F' }}>{(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span> },
              { key: 'cost_usd', header: 'Cost (USD)', render: (v) => <span className="font-mono text-nexus-text-secondary">${(v ?? 0).toFixed(4)}</span> },
              { key: 'last_used_at', header: 'Last used', render: (v) => v ? new Date(v).toLocaleString() : <span className="text-nexus-text-tertiary">—</span> },
            ]}
            data={usage || []}
            emptyMessage="No API-key traffic yet. Make a request to /api/v1/chat with the key in the Authorization header to populate this matrix."
          />
        </div>
      </NexusCard>

      {/* Usage Example */}
      <NexusCard variant="glass" hover={false}>
        <div className="p-6">
          <h3 className="font-heading text-lg font-semibold text-nexus-text-primary mb-4">
            Usage Example
          </h3>
          <pre className="bg-nexus-bg-primary rounded-lg p-4 text-sm text-nexus-text-primary overflow-x-auto">
            <code>{`curl -X POST "${process.env.REACT_APP_BACKEND_URL}/api/v1/chat" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {"role": "user", "content": "Hello!"}
    ],
    "stream": false
  }'`}</code>
          </pre>
        </div>
      </NexusCard>
    </div>
  );
};

export default PortalAPIKeys;
